/*
 * Rx configurator: progress rail + running total.
 *
 * Turns the three native variant options into a visible sequence with an end,
 * and keeps a live price in front of the customer so the jump from base to
 * fully-loaded is never a surprise at checkout.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE TRUTH LIVES
 *
 * Native options: the variant picker's inline <script type="application/json">
 * is the authoritative selected variant. Every probe on 2026-08-08 showed it
 * correct at all times, including while Globo's own price element was one step
 * behind. Read it, never scrape rendered price text.
 *
 * Globo options: Globo owns the surcharge and does NOT fold it into
 * [data-gpo-product-unit-price]. Measured on redford-rx: selecting
 * "Trivex (+ $50.00)" left the unit price at $275 and announced the surcharge
 * separately in .gpo-total ("Selections will add $50.00 to the price"). So:
 *
 *     total = variant price + Globo announced surcharge
 *
 * We parse .gpo-total for the announced amount rather than diffing rendered
 * prices, because inferring an app's intent from the pixels it just wrote has
 * burned us twice (once learning a stale write as a +$120 surcharge, once
 * reporting zero priced options on a product visibly selling +$50 upgrades).
 *
 * ---------------------------------------------------------------------------
 * TIMING
 *
 * Globo renders client-side and late, so its step cannot be counted at parse
 * time. A MutationObserver registers it whenever it lands. We never drive
 * anything off setTimeout/rAF polling: Chrome throttles both inside the theme
 * editor's occluded cross-origin iframe, which is exactly how the stale-price
 * bug survived so long.
 */
(function () {
  if (window.__madsonRxConfigurator) return;
  window.__madsonRxConfigurator = true;

  var MONEY = /(-?[\d,]+(?:\.\d{1,2})?)/;

  function parseMoney(text) {
    if (!text) return 0;
    var m = String(text).match(MONEY);
    if (!m) return 0;
    var n = parseFloat(m[1].replace(/,/g, ''));
    return isNaN(n) ? 0 : n;
  }

  function formatMoney(amount, currencySymbol) {
    var whole = Math.round(amount * 100) / 100;
    var str = whole % 1 === 0
      ? String(Math.round(whole))
      : whole.toFixed(2);
    return (currencySymbol || '$') + str.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }


  /* ---------------- idempotent DOM writes ----------------
   * Writing the same value still counts as a mutation, which would wake the
   * observer forever. Compare first; only touch the DOM on a real change.
   */
  function setText(el, value) {
    if (!el) return;
    var next = value == null ? '' : String(value);
    if (el.textContent !== next) el.textContent = next;
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (el.hidden !== hidden) el.hidden = hidden;
  }

  function setClass(el, name, on) {
    if (!el) return;
    if (el.classList.contains(name) !== on) el.classList.toggle(name, on);
  }

  function setAttr(el, name, value) {
    if (!el) return;
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function Configurator(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id') || '';
    this.currency = root.getAttribute('data-currency-symbol') || '$';
    this.railSteps = [];
    this.hasGloboStep = root.getAttribute('data-globo-step') === 'true';
    this.globoRegistered = false;
    this.init();
  }

  Configurator.prototype.scope = function () {
    // Everything lives inside the same product-information section.
    return this.root.closest('.shopify-section, [id^="shopify-section"]') || document;
  };

  Configurator.prototype.init = function () {
    this.railSteps = Array.prototype.slice.call(
      this.root.querySelectorAll('[data-rx-step]')
    );

    var self = this;
    document.addEventListener('variant:update', function () { self.update(); });

    // Native option inputs fire before variant:update settles; listening to both
    // means the rail never lags the click.
    this.scope().addEventListener('change', function (event) {
      if (event.target && event.target.matches('input[type="radio"], select')) {
        self.update();
      }
    });

    this.watchGlobo();
    this.update();
  };

  /* ---------------- Globo ---------------- */

  /**
   * Watch for Globo's widget.
   *
   * Globo re-renders wholesale on variant change, so we cannot bind to elements
   * that get replaced. An observer is the right tool, but the first version of
   * this watched document.body with subtree:true and called update() on EVERY
   * mutation. update() writes to the DOM, those writes are mutations, and the
   * observer fired again: a feedback loop. On the storefront the DOM settles
   * after load so it looked fine. The theme editor mutates continuously, so it
   * never settled and the editor hung on a blank frame.
   *
   * Three things keep it bounded now:
   *   1. observe the product section, not the whole document
   *   2. ignore any mutation originating inside our own output
   *   3. coalesce bursts into one update per microtask
   *
   * Plus setText/setAttr below only touch the DOM when the value really
   * changed, so a steady state produces no mutations at all.
   */
  Configurator.prototype.watchGlobo = function () {
    if (!this.hasGloboStep) return;
    var self = this;
    var target = this.scope();
    if (!target || target === document) target = document.body;
    if (!target) return;

    var observer = new MutationObserver(function (records) {
      var ours = true;
      for (var i = 0; i < records.length; i++) {
        var node = records[i].target;
        if (!self.owns(node)) { ours = false; break; }
      }
      if (ours) return;

      var app = document.querySelector('.gpo-app');
      if (app && !self.globoRegistered) {
        self.globoRegistered = true;
        setAttr(self.root, 'data-globo-ready', 'true');
      }
      self.schedule();
    });

    observer.observe(target, { childList: true, subtree: true });
    this.observer = observer;
  };

  /** True when a node is inside markup this script owns. */
  Configurator.prototype.owns = function (node) {
    if (!node) return false;
    var el = node.nodeType === 1 ? node : node.parentElement;
    if (!el) return false;
    if (this.root.contains(el)) return true;
    var summary = this.scope().querySelector('.rx-summary');
    return !!(summary && summary.contains(el));
  };

  /** Coalesce a burst of mutations into a single update. */
  Configurator.prototype.schedule = function () {
    if (this.queued) return;
    this.queued = true;
    var self = this;
    Promise.resolve().then(function () {
      self.queued = false;
      self.update();
    });
  };

  Configurator.prototype.globoState = function () {
    if (!this.hasGloboStep) return null;

    var app = document.querySelector('.gpo-app');
    if (!app) return { present: false, chosen: null, surcharge: 0 };

    var checked = app.querySelector('.gpo-choicelist input:checked');
    var chosen = null;
    if (checked) {
      var label = checked.closest('.gpo-choicelist');
      var value = label && label.querySelector('.choicelist-item__value');
      // .addOn is nested inside the value span and renders "(+ $50.00)".
      var addOn = value && value.querySelector('.addOn');
      var text = value ? value.textContent : checked.value;
      if (addOn) text = text.replace(addOn.textContent, '');
      chosen = (text || '').trim() || checked.value;
    }

    // Globo announces the surcharge separately; it is not in the unit price.
    var totalEl = app.querySelector('.gpo-total');
    var surcharge = 0;
    if (totalEl && /add/i.test(totalEl.textContent)) {
      surcharge = parseMoney(totalEl.textContent);
    }

    return { present: true, chosen: chosen, surcharge: surcharge };
  };

  /* ---------------- native variant ---------------- */

  Configurator.prototype.variantPrice = function () {
    var picker = this.scope().querySelector('variant-picker');
    var tag = picker && picker.querySelector('script[type="application/json"]');
    if (!tag) return null;
    try {
      var v = JSON.parse(tag.textContent);
      return v && typeof v.price === 'number' ? v.price / 100 : null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Which value is selected for a 1-based option position.
   *
   * The picker gives every input `data-input-id="<option.position>-<index>"`
   * (snippets/variant-main-picker.liquid:485 for cards, :604 for buttons).
   * That prefix is the only stable position hook it emits; the fieldsets carry
   * `data-fieldset-index` (0-based) and a handleized `data-option-id`, neither
   * of which is a position. Verified against the source, not assumed.
   *
   * Returns null when the option has a single value, because Horizon renders
   * no input at all in that case and resolves it implicitly (:318).
   */
  Configurator.prototype.chosenFor = function (position) {
    var input = this.scope().querySelector(
      'input[data-input-id^="' + position + '-"]:checked'
    );
    return input ? (input.value || '').trim() : null;
  };

  /* ---------------- render ---------------- */

  Configurator.prototype.update = function () {
    var self = this;
    var globo = this.globoState();
    var chosenCount = 0;
    var firstOpen = -1;

    this.railSteps.forEach(function (step, index) {
      var kind = step.getAttribute('data-rx-step');
      var chosen = null;

      if (kind === 'globo') {
        chosen = globo && globo.present ? globo.chosen : null;
        setHidden(step, !(globo && globo.present));
      } else {
        // Single-value options render no input and resolve implicitly, so the
        // block stamps the value server-side. Without this they would never
        // register as chosen and the page could never reach a complete state.
        chosen = step.getAttribute('data-rx-resolved')
          || self.chosenFor(step.getAttribute('data-option-position'));
      }

      if (chosen) chosenCount++;
      else if (firstOpen === -1 && !step.hidden) firstOpen = index;

      setClass(step, 'rx-rail__step--done', !!chosen);
      var valueEl = step.querySelector('[data-rx-step-value]');
      setText(valueEl, chosen || '');
    });

    this.railSteps.forEach(function (step, index) {
      setClass(step, 'rx-rail__step--active', index === firstOpen);
      var valueEl = step.querySelector('[data-rx-step-value]');
      if (valueEl && index === firstOpen && !valueEl.textContent) {
        setText(valueEl, step.getAttribute('data-rx-prompt') || '');
      }
    });

    var visible = this.railSteps.filter(function (s) { return !s.hidden; });
    var complete = visible.length > 0 && chosenCount >= visible.length;

    this.renderTotals(globo, chosenCount, visible.length, complete);
  };

  Configurator.prototype.renderTotals = function (globo, count, total, complete) {
    var base = this.variantPrice();
    var scope = this.scope();
    var surcharge = globo && globo.present ? globo.surcharge : 0;

    var totalEl = scope.querySelector('[data-rx-total]');
    var surchargeRow = scope.querySelector('[data-rx-surcharge-row]');
    var surchargeVal = scope.querySelector('[data-rx-surcharge-value]');
    var surchargeKey = scope.querySelector('[data-rx-surcharge-key]');
    var progressEl = scope.querySelector('[data-rx-progress]');
    var doneEl = scope.querySelector('[data-rx-complete]');

    if (base !== null && totalEl) {
      setText(totalEl, formatMoney(base + surcharge, this.currency));
    }

    if (surchargeRow) {
      if (surcharge > 0) {
        setHidden(surchargeRow, false);
        setText(surchargeVal, '+' + formatMoney(surcharge, this.currency));
        if (globo && globo.chosen) setText(surchargeKey, globo.chosen);
      } else {
        setHidden(surchargeRow, true);
      }
    }

    if (progressEl) {
      setText(progressEl, complete
        ? progressEl.getAttribute('data-rx-complete-label') || 'Ready'
        : count + ' of ' + total + ' chosen');
    }

    setHidden(doneEl, !complete);
    setAttr(this.root, 'data-rx-complete-state', complete ? 'true' : 'false');
  };

  function boot() {
    document.querySelectorAll('[data-rx-configurator]').forEach(function (root) {
      if (root.__rxBound) return;
      root.__rxBound = true;
      new Configurator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Section re-render in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
